/**
 * Due dates, and how late a return is — to the day it was filed, not to today.
 * @file src/web/features/admin/lib/due-dates.ts
 *
 * "17 days overdue" was shown beside a return filed on the 18th that fell due
 * on the 11th, because the count always ran to today. A filed return was late
 * by the days between the due date and the day it was filed, and stays so; only
 * a return not yet filed is still getting later. `today` is passed in so the
 * rule can be tested on any date.
 *
 * @llm-rule WHEN: Saying how late or how soon a return is
 * @llm-rule AVOID: Counting a filed return to today
 */

/** Calendar days from `a` to `b` (ISO dates), whole days, DST-proof. */
export function daysBetween(a: string, b: string): number {
  const ms = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  return Math.round((ms(b) - ms(a)) / 86_400_000);
}

/**
 * Statutory due date for a monthly filer: the `day` of the month AFTER the
 * period (GSTR-1 the 11th, GSTR-3B the 20th). QRMP dates differ, so callers
 * say nothing for them rather than the wrong thing. Holiday extensions and
 * notified relaxations are not known here.
 */
export function dueDateOf(period: string, day: number): string | null {
  if (!/^\d{6}$/.test(period)) return null;
  const mm = Number(period.slice(0, 2)), yy = Number(period.slice(2));
  if (mm < 1 || mm > 12) return null;
  const y = mm === 12 ? yy + 1 : yy, m = mm === 12 ? 1 : mm + 1;
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export type Due =
  | { kind: 'upcoming'; dueOn: string; days: number }
  | { kind: 'today'; dueOn: string }
  | { kind: 'overdue'; dueOn: string; days: number }
  | { kind: 'filed-on-time'; dueOn: string; filedOn: string }
  | { kind: 'filed-late'; dueOn: string; filedOn: string; days: number };

/**
 * Where a return stands against its due date.
 * `filedOn` is the ISO date it was filed (only for a return that really is
 * filed); without it the return is still open and is counted to `today`.
 */
export function dueStatus(a: { period: string; day: number; filedOn?: string | null; today: string }): Due | null {
  const dueOn = dueDateOf(a.period, a.day);
  if (!dueOn) return null;
  const filedOn = a.filedOn && /^\d{4}-\d{2}-\d{2}/.test(a.filedOn) ? a.filedOn.slice(0, 10) : null;
  if (filedOn) {
    const late = daysBetween(dueOn, filedOn);
    return late > 0 ? { kind: 'filed-late', dueOn, filedOn, days: late } : { kind: 'filed-on-time', dueOn, filedOn };
  }
  const n = daysBetween(a.today, dueOn);
  return n > 0 ? { kind: 'upcoming', dueOn, days: n } : n === 0 ? { kind: 'today', dueOn } : { kind: 'overdue', dueOn, days: -n };
}

const fmt = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { dateStyle: 'medium' });
const plural = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

/** The line shown beside a return. */
export function dueText(d: Due | null): string | null {
  if (!d) return null;
  switch (d.kind) {
    case 'upcoming': return `Due ${fmt(d.dueOn)} — in ${plural(d.days)}`;
    case 'today': return `Due today, ${fmt(d.dueOn)}`;
    case 'overdue': return `Due ${fmt(d.dueOn)} — ${plural(d.days)} overdue`;
    case 'filed-on-time': return `Due ${fmt(d.dueOn)} — filed ${fmt(d.filedOn)}, on time`;
    case 'filed-late': return `Due ${fmt(d.dueOn)} — filed ${fmt(d.filedOn)}, ${plural(d.days)} late`;
  }
}
